import { formatGramMlWeightLabel, gramsToMl, resolveGramsPerMl, roundTo2, shouldShowDistinctMlValue, toPositiveNumber, unitAmountToMl } from '@/utils/gram-ml';

export type ExtractedIngredientUnitMetadata = {
  units: string[];
  labelsByKey: Record<string, string>;
  weightsByKey: Record<string, number>;
  mlWeightsByKey: Record<string, number>;
  gramsPerMl: number | null;
};

export const normalizeIngredientUnitKey = (unit: string): string => unit.trim().toLowerCase();

export const normalizeIngredientUnits = (units: unknown): string[] => {
  if (!Array.isArray(units)) return [];

  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const rawUnit of units) {
    const unit = String(rawUnit ?? '').trim();
    if (!unit) continue;
    const key = normalizeIngredientUnitKey(unit);
    if (seen.has(key)) continue;
    seen.add(key);
    normalized.push(unit);
  }

  return normalized;
};

const formatIngredientUnitLabel = (
  unit: string,
  gramsPerUnit: unknown,
  mlPerUnit: unknown,
  gramsPerMl: unknown,
): string => {
  const grams = toPositiveNumber(gramsPerUnit);
  const ml = toPositiveNumber(mlPerUnit);

  if (grams != null) {
    const gramsText = `${roundTo2(grams)}g`;
    if (ml != null && shouldShowDistinctMlValue(grams, ml)) {
      return `${unit} (${gramsText} / ${roundTo2(ml)}ml)`;
    }
    return formatGramMlWeightLabel(unit, grams, gramsPerMl);
  }

  if (ml != null) {
    return `${unit} (${roundTo2(ml)}ml)`;
  }

  return unit;
};

export const extractIngredientUnitMetadata = (response: any): ExtractedIngredientUnitMetadata => {
  const labelsByKey: Record<string, string> = {};
  const weightsByKey: Record<string, number> = {};
  const mlWeightsByKey: Record<string, number> = {};
  const unitsByKey: Record<string, string> = {};
  const rememberUnit = (rawUnit: unknown): string | null => {
    const unit = String(rawUnit ?? '').trim();
    if (!unit) return null;
    const key = normalizeIngredientUnitKey(unit);
    if (!unitsByKey[key]) {
      unitsByKey[key] = unit;
    }
    return key;
  };

  const explicitUnitOptions = Array.isArray(response?.unitOptions) ? response.unitOptions : [];
  for (const option of explicitUnitOptions) {
    const value = String(option?.value ?? '').trim();
    const label = String(option?.label ?? '').trim();
    const key = rememberUnit(value);
    if (!key || !label) continue;
    labelsByKey[key] = label;
  }

  rememberUnit(response?.unit);
  rememberUnit((response as { requestedUnit?: unknown } | undefined)?.requestedUnit);

  for (const unit of response?.possibleUnits ?? []) {
    rememberUnit(unit);
  }

  if (response?.servingWeights && typeof response.servingWeights === 'object') {
    for (const unit of Object.keys(response.servingWeights)) {
      rememberUnit(unit);
    }
  }

  if (response?.servingMlWeights && typeof response.servingMlWeights === 'object') {
    for (const unit of Object.keys(response.servingMlWeights)) {
      rememberUnit(unit);
    }
  }

  const possibleUnitWeights = Array.isArray(response?.possibleUnitWeights) ? response.possibleUnitWeights : [];
  for (const entry of possibleUnitWeights) {
    const key = rememberUnit(entry?.unit);
    const grams = toPositiveNumber(entry?.grams);
    if (!key || grams == null) continue;
    weightsByKey[key] = roundTo2(grams);
  }

  if (response?.servingWeights && typeof response.servingWeights === 'object') {
    for (const [unit, gramsValue] of Object.entries(response.servingWeights as Record<string, unknown>)) {
      const key = rememberUnit(unit);
      const grams = toPositiveNumber(gramsValue);
      if (!key || grams == null || weightsByKey[key] != null) continue;
      weightsByKey[key] = roundTo2(grams);
    }
  }

  const possibleUnitMlWeights = Array.isArray(response?.possibleUnitMlWeights) ? response.possibleUnitMlWeights : [];
  for (const entry of possibleUnitMlWeights) {
    const key = rememberUnit(entry?.unit);
    const ml = toPositiveNumber(entry?.ml);
    if (!key || ml == null) continue;
    mlWeightsByKey[key] = roundTo2(ml);
  }

  if (response?.servingMlWeights && typeof response.servingMlWeights === 'object') {
    for (const [unit, mlValue] of Object.entries(response.servingMlWeights as Record<string, unknown>)) {
      const key = rememberUnit(unit);
      const ml = toPositiveNumber(mlValue);
      if (!key || ml == null || mlWeightsByKey[key] != null) continue;
      mlWeightsByKey[key] = roundTo2(ml);
    }
  }

  const gramsPerMl = resolveGramsPerMl(
    response?.servingWeights && typeof response.servingWeights === 'object'
      ? response.servingWeights as Record<string, unknown>
      : weightsByKey,
    response?.gramsPerMl,
  );

  for (const [key, unit] of Object.entries(unitsByKey)) {
    if (labelsByKey[key]) continue;
    labelsByKey[key] = formatIngredientUnitLabel(
      unit,
      weightsByKey[key],
      mlWeightsByKey[key],
      gramsPerMl,
    );
  }

  return {
    units: normalizeIngredientUnits(Object.values(unitsByKey)),
    labelsByKey,
    weightsByKey,
    mlWeightsByKey,
    gramsPerMl,
  };
};

export const resolveIngredientUnitMlWeight = (input: {
  unit: unknown;
  unitWeightMl?: unknown;
  unitWeightGrams?: unknown;
  gramsPerMl?: unknown;
  mlWeightsByKey?: Record<string, number>;
}): number | null => {
  const explicitMl = toPositiveNumber(input.unitWeightMl);
  if (explicitMl != null) return roundTo2(explicitMl);

  const unit = String(input.unit ?? '').trim();
  const volumeUnitMl = unitAmountToMl(1, unit);
  if (volumeUnitMl != null) return roundTo2(volumeUnitMl);

  const standardMl = toPositiveNumber(input.mlWeightsByKey?.[normalizeIngredientUnitKey(unit)]);
  if (standardMl != null) return roundTo2(standardMl);

  return gramsToMl(input.unitWeightGrams, input.gramsPerMl);
};

export const resolveIngredientAmountMl = (input: {
  amount: unknown;
  unit: unknown;
  unitWeightMl?: unknown;
  unitWeightGrams?: unknown;
  gramsPerMl?: unknown;
  mlWeightsByKey?: Record<string, number>;
  amountGrams?: unknown;
}): number | null => {
  const amount = toPositiveNumber(input.amount);
  if (amount == null) return null;

  const unitMlWeight = resolveIngredientUnitMlWeight(input);
  if (unitMlWeight != null) {
    return roundTo2(amount * unitMlWeight);
  }

  return gramsToMl(input.amountGrams, input.gramsPerMl);
};
